import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { generate } from 'shortid';
import FlexTableCell from '../FlexTableCell';
import { FlexTableCellItem } from '../../types';
import styles from '../../styles';
import { TableState } from '../../utils';

type FlexTableRowProps = {
  row: FlexTableCellItem[];
  rowIndex: number;
  cellStyles?: Record<string, any>;
  headingStyles?: Record<string, any>;
  rowBordered?: boolean;
  tableState: TableState;
  cellBordered?: boolean;
  isLast?: boolean;
};

const FlexTableRow = ({
  row,
  rowIndex,
  headingStyles,
  cellStyles,
  rowBordered = true,
  tableState,
  cellBordered = false,
  isLast = false
}: FlexTableRowProps) => {
  const { itemsPerPage, rowToPageMap } = tableState;

  const addRowToPage = (pageNumber: number) => {
    if (!itemsPerPage[pageNumber]) {
      itemsPerPage[pageNumber] = [];
    }
    if (!itemsPerPage[pageNumber].includes(rowIndex)) {
      itemsPerPage[pageNumber].push(rowIndex);
    }
  };

  const initializeRow = (pageNumber: number) => {
    rowToPageMap[rowIndex] = pageNumber;
    addRowToPage(pageNumber);
  };

  const moveRowToNewPage = (pageNumber: number, currentPage: number) => {
    // Remove rowIndex from the old page
    if (itemsPerPage[currentPage]) {
      itemsPerPage[currentPage] = itemsPerPage[currentPage].filter(
        idx => idx !== rowIndex
      );
    }

    // Add rowIndex to the new page
    addRowToPage(pageNumber);

    // Update the row to page mapping
    rowToPageMap[rowIndex] = pageNumber;
  };

  const setItemPerPage = (pageNumber: number): null => {
    const currentPage = rowToPageMap[rowIndex];

    // If this is the first time we see this row, initialize it
    if (currentPage === undefined) {
      initializeRow(pageNumber);
      return null;
    }

    // react-pdf resolves layout over several passes; a row can be tentatively
    // placed on one page and then relocated (up or down) before the layout
    // settles. Always track the latest page so rowToPageMap reflects the final
    // layout and converges, instead of staying stuck on the highest page ever
    // seen (which makes the continued-text indicator fire mid-page).
    if (pageNumber !== currentPage) {
      moveRowToNewPage(pageNumber, currentPage);
    }

    return null;
  };

  const borderlessStyle: any = {};
  if (!rowBordered) {
    borderlessStyle.borderTop = 'none';
  }

  let tableHeaderRowStyle: any = {};
  let shouldShowHeaderBorder = false;
  // If table does not have row borders, then add a border under the header row instead
  if (rowIndex === 1) {
    tableHeaderRowStyle = { ...styles.tableHeaderRow };
    if (!rowBordered) {
      tableHeaderRowStyle.alignItems = 'flex-start';
      shouldShowHeaderBorder = true;
    }
  }

  const lastRowStyle = cellBordered && isLast ? styles.tableLastRow : {};

  const finalRowStyle = {
    ...styles.tableRow,
    ...borderlessStyle,
    ...tableHeaderRowStyle,
    ...lastRowStyle
  };

  return (
    <View key={`table-row-${generate()}`}>
      <View>
        <View style={finalRowStyle}>
          {row.map((cell, index) => (
            <FlexTableCell
              key={`table-cell-${generate()}`}
              cell={cell}
              rowIndex={rowIndex}
              isLast={index === row.length - 1}
              cellStyles={cellStyles}
              headingStyles={headingStyles}
              showBorder={cellBordered}
            />
          ))}
        </View>
        {/* This is used to track the number of items per page and row index to page mapping */}
        <Text render={({ pageNumber }) => setItemPerPage(pageNumber)} />
        {shouldShowHeaderBorder ? (
          <View style={styles.headerRowBorder} />
        ) : (
          <View />
        )}
      </View>
    </View>
  );
};

export default FlexTableRow;
